import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiFilter } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { apiGetBooks, apiGetCategories } from '../api';
import SEO from '../components/common/SEO';
import Pagination from '../components/common/Pagination';
import ShopFilters from '../components/shop/ShopFilters';
import { useToast } from '../context/ToastContext';
import { ShopGrid } from './shop/ShopGrid';
import { ShopMobileFilters } from './shop/ShopMobileFilters';
import SearchResultsList from './shop/SearchResultsList';
import { useShopPriceFilters } from '../hooks/useShopPriceFilters';
import { isLanguageBrowse, isLanguageCategory } from '../utils/languageNavigation';

const ITEMS_PER_PAGE = 100;
const validSort = (value) =>
  ['bestseller', 'newest'].includes(value) ? value : 'relevance';

const ServerShop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const languageBrowse = isLanguageBrowse(searchParams);
  const category = languageBrowse ? '' : searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const language = searchParams.get('language') || '';
  const [priceRange, setPriceRange] = useState('all');
  const [sort, setSort] = useState(validSort(searchParams.get('sort')));
  const [page, setPage] = useState(1);
  const [books, setBooks] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestVersion, setRequestVersion] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { priceOptions, priceRanges } = useShopPriceFilters();

  useEffect(() => {
    apiGetCategories()
      .then((data) => setCategories((data.categories || []).map((item) => item.name)))
      .catch((requestError) =>
        addToast(requestError.message || 'Categories could not be loaded', 'error')
      );
  }, [addToast]);

  useEffect(() => {
    const requestedSort = validSort(searchParams.get('sort'));
    setSort(requestedSort);
    setPage(1);
    if (isLanguageBrowse(searchParams)) setPriceRange('all');
  }, [searchParams]);

  const query = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(ITEMS_PER_PAGE),
    });
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    if (language) params.set('language', language);
    if (searchParams.get('preorder') === 'true') params.set('preorder', 'true');
    const prices = priceRanges[priceRange];
    if (prices?.min != null) params.set('minPrice', String(prices.min));
    if (prices?.max != null) params.set('maxPrice', String(prices.max));
    const sortMap = {
      priceLow: 'price_low',
      priceHigh: 'price_high',
      rating: 'rating',
      newest: 'newest',
    };
    if (sort === 'bestseller') params.set('bestseller', 'true');
    else if (sort === 'newest') {
      params.set('newArrival', 'true');
      params.set('sort', 'newest');
    } else if (sortMap[sort]) params.set('sort', sortMap[sort]);
    return params.toString();
  }, [category, language, page, priceRange, priceRanges, search, searchParams, sort]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    apiGetBooks(query)
      .then((data) => {
        if (active) {
          setBooks(data.books || []);
          setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
        }
      })
      .catch((requestError) => {
        if (active) {
          setBooks([]);
          setError(requestError.message || 'Failed to load products');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [query, requestVersion]);

  const changeFilter = useCallback(
    (setter) => (value) => {
      setter(value);
      setPage(1);
    },
    []
  );
  const changeCategory = useCallback(
    (value) => {
      setPage(1);
      if (isLanguageCategory(value)) {
        setSearchParams({ browse: 'languages' });
        return;
      }
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        if (value) nextParams.set('category', value);
        else nextParams.delete('category');
        nextParams.delete('browse');
        nextParams.delete('language');
        return nextParams;
      });
    },
    [setSearchParams]
  );
  const clearFilters = useCallback(() => {
    setPriceRange('all');
    setSort('relevance');
    setPage(1);
    setSearchParams({});
  }, [setSearchParams]);
  const filterProps = {
    categories,
    selectedCategory: languageBrowse ? 'Languages' : category,
    onCategoryChange: changeCategory,
    priceRange,
    priceOptions,
    onPriceChange: changeFilter(setPriceRange),
    onClearAll: clearFilters,
  };
  const start = pagination.total ? (page - 1) * ITEMS_PER_PAGE + 1 : 0;
  const end = Math.min(page * ITEMS_PER_PAGE, pagination.total || 0);

  return (
    <>
      <SEO
        title="Shop Products"
        description="Browse and filter products by category, language, and price."
      />
      <div className="w-full bg-white border-b sticky top-0 z-30">
        <header className="flex items-center">
          <div className="hidden lg:flex items-center justify-between w-64 px-4 py-3 border-r">
            <h3 className="font-bold">Filters</h3>
            <button onClick={clearFilters} className="text-sm text-cyan-500">
              Clear All
            </button>
          </div>
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
            <p className="text-sm min-w-0">
              {start}-{end} of {pagination.total || 0} results for{' '}
              <span className="text-orange-600 font-semibold">
                &quot;
                {search ||
                  (languageBrowse
                    ? language || 'All Languages'
                    : category || 'All Products')}
                &quot;
              </span>
            </p>
            <div className="flex items-center justify-between sm:justify-end gap-2 min-w-0">
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="lg:hidden border rounded px-3 py-1.5 inline-flex items-center gap-1"
              >
                <FiFilter /> Filters
              </button>
              <select
                value={sort}
                onChange={(event) => {
                  setSort(event.target.value);
                  setPage(1);
                }}
                className="border rounded px-2 sm:px-3 py-1.5 text-sm min-w-0 max-w-[180px]"
              >
                <option value="relevance">Sort By : Featured</option>
                <option value="bestseller">Bestsellers</option>
                <option value="priceLow">Price: Low to High</option>
                <option value="priceHigh">Price: High to Low</option>
                <option value="rating">Customer Rating</option>
                <option value="newest">Newest Arrivals</option>
              </select>
            </div>
          </div>
        </header>
      </div>
      <div className="flex">
        <aside
          aria-label="Shop filters"
          className="shop-filter-sidebar hidden lg:block w-64 shrink-0 bg-white border-r sticky top-[53px] h-[calc(100vh-53px)] overflow-y-auto"
        >
          <ShopFilters {...filterProps} hideHeader />
        </aside>
        <ShopMobileFilters
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          filterProps={filterProps}
        />
        <main className="flex-1 min-w-0 bg-gray-50 p-3 min-h-screen sm:p-6">
          {error ? (
            <div className="text-center py-16">
              <p className="text-red-600">{error}</p>
              <button
                onClick={() => setRequestVersion((current) => current + 1)}
                className="mt-3 text-orange-600"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {search ? (
                <SearchResultsList
                  loading={loading}
                  books={books}
                  onClearFilters={clearFilters}
                />
              ) : (
                <ShopGrid
                  loading={loading}
                  filteredBooks={books}
                  visibleBooks={books}
                  hasMore={false}
                  onClearFilters={clearFilters}
                />
              )}
              <Pagination
                page={page}
                pages={pagination.pages}
                onChange={setPage}
                className="mt-8"
              />
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default ServerShop;
